// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a chip on "when can we meet?" opens (docs/konvo-assistant/ready-list.md point 4).
// The mail card's time picker (MailMeetingPicker: one row of times per day, the busy ones
// striped, who is free underneath), the reply it will write in the chat's language, then
// "Reply with <time>" (into this chat's message box, never sent) and "Hold <time>" (a busy
// block in the reader's calendar, with Undo on the chip).

import styles from '@app/features/konvo_assistant/components/action_chip/ActionChipMenu.module.css';
import {T} from '@app/features/konvo_assistant/components/action_chip/ActionChipText';
import type {ActionChipController} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import {MailMeetingPicker} from '@app/features/konvo_mail/components/MailMeetingPicker';
import {shortTimeText} from '@app/features/konvo_mail/state/MailMeeting';
import {ArrowBendUpLeftIcon, CalendarBlankIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

export const MeetingChipPanel = observer(({chip, onClose}: {chip: ActionChipController; onClose: () => void}) => {
	const {i18n} = useLingui();
	const meeting = chip.options.meeting;
	const choice = chip.meetingChoice;
	if (!meeting || !choice) return null;
	const option = chip.meetingOption;
	const time = option ? shortTimeText(option.start, getCurrentLocale()) : null;
	const reply = option ? chip.meetingReply : null;
	const busy = chip.phase !== 'idle';
	return (
		<div
			className={clsx(styles.menu, styles.meetingPanel)}
			role="dialog"
			aria-label={i18n._(T.pickTime)}
			onKeyDown={(event) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					onClose();
				}
			}}
			data-flx="konvo-assistant.action-chip.meeting"
		>
			<MailMeetingPicker
				action={meeting}
				choice={choice}
				heading={i18n._(T.pickTime)}
				phone={MobileLayout.enabled}
				onPick={chip.pickMeetingTime}
				onDuration={chip.pickMeetingDuration}
			/>
			{reply && (
				<p className={styles.meetingReply} data-flx="konvo-assistant.action-chip.meeting.reply-text">
					{reply}
				</p>
			)}
			<div className={styles.meetingActions}>
				<button
					type="button"
					className={styles.meetingPrimary}
					disabled={!time || busy}
					onClick={() => {
						void chip.replyWithTime().then((done) => {
							if (done) onClose();
						});
					}}
					data-flx="konvo-assistant.action-chip.meeting.reply"
				>
					<ArrowBendUpLeftIcon size={15} />
					{time ? i18n._(T.replyWith, {time}) : i18n._(T.pickTime)}
				</button>
				{time && (
					<button
						type="button"
						className={styles.meetingSecondary}
						disabled={busy}
						onClick={() => {
							onClose();
							void chip.holdTime(i18n);
						}}
						data-flx="konvo-assistant.action-chip.meeting.hold"
					>
						<CalendarBlankIcon size={15} />
						{i18n._(T.holdTime, {time})}
					</button>
				)}
			</div>
		</div>
	);
});
